import React from "react";
import ReactDOM from "react-dom/client";
import "./index.scss";
import reportWebVitals from "./reportWebVitals";
import { MotifTheme } from "@ey-xd/motif-react";
import {
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
} from "react-router-dom";
import { AppRoutes } from "./routes/app-routes";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "./configs/authConfig";
import { Provider } from "react-redux";
import store from "./store/store";
import { UserManageemntService } from "./api/services/users.service";
import { setUserRoles } from "./store/slices/userSlice";

const getLoginScopes = () => {
  const configuredScope = (process.env.REACT_APP_TOKEN_SCOPE || "").trim();

  if (configuredScope && configuredScope !== "t") {
    return ["openid", "profile", "email", configuredScope];
  }

  return [
    "openid",
    "profile",
    "email",
  ];
};

const isAadConfigurationError = (error) => {
  const message = `${error?.errorCode || ""} ${error?.message || ""}`;
  return /AADSTS50194|invalid_request/i.test(message);
};

// Clear MSAL interaction state from sessionStorage to fix stale login attempts
const clearMsalInteractionState = () => {
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.startsWith("msal.") || key.includes("msal"))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

const router = createBrowserRouter(createRoutesFromElements(AppRoutes));

const root = ReactDOM.createRoot(document.getElementById("root"));

// Initialize MSAL and handle any redirect response BEFORE rendering the app
const initializeMsal = async () => {
  let shouldRender = true;

  try {
    // Initialize the MSAL instance
    await msalInstance.initialize();

    // Handle redirect response - this MUST complete before rendering
    const response = await msalInstance.handleRedirectPromise();

    if (response) {
      // User just logged in via redirect, set as active account
      //  console.log("Login successful, account:", response.account);
      msalInstance.setActiveAccount(response.account);

      // Fetch user roles asynchronously (non-blocking) - handled by UserRolesFetcher in App.jsx
    } else {
      // Check if there's already an account from a previous session
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        // console.log("Found existing account:", accounts[0]);
        msalInstance.setActiveAccount(accounts[0]);

        // User roles will be fetched asynchronously by UserRolesFetcher in App.jsx
      } else {
        // No account found, trigger login redirect
        //  console.log("No account found, redirecting to login...");
        try {
          const scopes = getLoginScopes();
          // Legacy login scopes:
          // scopes: ["openid", "profile", "email","https://storage.azure.com/user_impersonation"],
          await msalInstance.loginRedirect({
            scopes,
            // prompt: "consent", // Force consent screen to appear
          });
          // console.log(
          //   "[MSAL Init] loginRedirect called, should be redirecting...",
          // );
          shouldRender = false; // Don't render, we're redirecting
        } catch (loginError) {
          console.error("[MSAL Init] Login error:", loginError);
          // Check if it's an interaction_in_progress error - clear state and retry
          if (loginError.errorCode === "interaction_in_progress") {
            // console.log(
            //   "[MSAL Init] Clearing stale interaction state and retrying...",
            // );
            clearMsalInteractionState();
            // Retry login after clearing state
            try {
              const scopes = getLoginScopes();
              await msalInstance.loginRedirect({ scopes });
              shouldRender = false;
            } catch (retryError) {
              console.error("[MSAL Init] Retry login failed:", retryError);
              shouldRender = true; // Give up and render the app
            }
          } else {
            console.error("[MSAL Init] Login redirect error:", loginError);
          }
        }
      }
    }
  } catch (error) {
    console.error("[MSAL Init] Initialization error:", error);

    // Check if it's an interaction_in_progress error - render the app anyway
    if (error.errorCode === "interaction_in_progress") {
      // console.log(
      //   "[MSAL Init] Interaction already in progress, rendering app...",
      // );
      shouldRender = true;
    } else if (isAadConfigurationError(error)) {
      console.error(
        "[MSAL] Single-tenant app cannot use /common. Configure REACT_APP_AZURE_TENANT_ID with your tenant ID/domain.",
      );
    }

    // If consent error, trigger login with consent prompt
    if (
      error.errorCode === "consent_required" ||
      error.errorCode === "interaction_required" ||
      error.errorCode === "invalid_grant" ||
      isAadConfigurationError(error)
    ) {
      try {
        const scopes = getLoginScopes();
        // Legacy consent scopes:
        // scopes: ["openid", "profile", "User.Read"],
        await msalInstance.loginRedirect({
          scopes,
          prompt: "consent",
        });
        shouldRender = false;
      } catch (loginError) {
        // Handle interaction_in_progress gracefully - render the app
        if (loginError.errorCode === "interaction_in_progress") {
          // console.log(
          //   "[MSAL Init] Consent interaction already in progress, rendering app...",
          // );
          shouldRender = true;
        } else {
          console.error("[MSAL Init] Login redirect error:", loginError);
        }
      }
    }
  }

  // Render the app (unless we're actively redirecting to login)
  // console.log("[MSAL Init] Should render:", shouldRender);
  if (shouldRender) {
    root.render(
      <Provider store={store}>
        <MsalProvider instance={msalInstance}>
          <MotifTheme variant="dark" />
          <RouterProvider router={router} />
        </MsalProvider>
      </Provider>,
    );
  }
};

initializeMsal();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
